import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { formatInTimeZone } from 'date-fns-tz'
import { Elements } from '@stripe/react-stripe-js'
import { PlusCircleIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { useGetInboxesWithCustom } from '@/api/chat'
import { useDeleteCustomChatInbox } from '@/api/chat/delete-custom-inbox'
import { popupFactory } from '@/factories'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { getApiErrorMessage } from '@/utils'

import {
  selectInvoices,
  dispatchUpdateChatInboxes,
  dispatchUpdateSmsPackage,
  getInvoicesWithCount,
  dispatchGetChatInboxes,
} from '../../../reducers/chatReducer'
import { selectUserRole } from '../../../reducers/userDataReducer'
import FeaturedTable from '../../FeaturedTable/FeaturedTable'
import SearchField from '../../FormLegacy/FormFieldsV2/SearchField'
import ConfirmPopup from '../../Popups/ConfirmPopup'
import PaymentModal from '../../Payment/PaymentModal/PaymentModal'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import API from '../../../api/api'

import PackageStatusInfo from './PackageStatusInfo/PackageStatusInfo'
import SectionSystemNumbers from './SectionSystemNumbers/SectionSystemNumbers'
import SelectedSystemNumber from './SelectedSystemNumber/SelectedSystemNumber'
import {
  chatInboxesTableColumns,
  chatInvoicesTableColumns,
  tooltipInfo,
  stripePromise,
} from './utils'
import SmsPackagesWindow from './SmsPackagesWindow/SmsPackagesWindow'
import InvoicesFullView from './InvoicesFullView'
import SelectedInboxDetails from './SelectedInboxDetails'
import InboxChatFormModal from './InboxChatFormModal'

const InboxSettings = ({
  recentInvoices,
  currentBuilding,
  dispatchUpdateChatInboxes,
  dispatchUpdateSmsPackage,
  userRole,
  getInvoicesWithCount,
}) => {
  const { t } = useTranslation(['chat'])
  const [search, setSearch] = useState('')
  const [selectedInboxId, setSelectedInboxId] = useState(null)
  const [invoicesFullView, setInvoicesFullView] = useState(false)
  const [selectedSystemNumberRow, setSelectedSystemNumberRow] = useState(null)
  const [cancelSubWindowVisibility, setCancelSubWindowVisibility] = useState(false)
  const [smsPackageWindowVisibility, setSmsPackageWindowVisibility] = useState(false)
  const [smsPackages, setSmsPackages] = useState([])
  const [numberPackage, setNumberPackage] = useState(null)
  const [paymentModalVisible, setPaymentModalVisible] = useState(false)
  const [selectedPackageSms, selectPackageSms] = useState(null)
  const [inboxChatFormModalVisible, setInboxChatFormModalVisible] = useState(false)
  const [selectedCustomInbox, setSelectedCustomInbox] = useState(null)

  const { mutateAsync: deleteCustomChatInboxAsync } = useDeleteCustomChatInbox()

  const {
    data: chatInboxes,
    smsPackage: buildingSmsPackage,
    refetch,
  } = useGetInboxesWithCustom({
    buildingId: currentBuilding?.id,
    chatCustom: currentBuilding?.chatCustom,
  })

  const { displaySnackbar } = useSnackbar()

  const handleActionInbox = ({ data, mode }) => {
    switch (mode) {
      case 'settings':
        if (data.type === 'CUST') {
          setSelectedCustomInbox(data)
          setInboxChatFormModalVisible(true)
          return
        }
        setSelectedInboxId(data.id)
        return
      case 'delete':
        popupFactory.open(ConfirmationPopup, {
          children: t('chat:inboxSettings.deleteConfirmBody'),
          confirmText: t('chat:inboxSettings.deleteInbox'),
          onConfirm: async () => {
            await deleteCustomChatInboxAsync(
              { id: data.id },
              {
                onSuccess: () => {
                  refetch()
                  displaySnackbar(t('chat:inboxSettings.deleteSuccess'))
                },
                onError: (error) => {
                  displaySnackbar(getApiErrorMessage(error), true)
                },
              },
            )
          },
          title: t('chat:inboxSettings.deleteConfirmTitle', { name: data.name }),
          variant: 'danger',
        })
        return
      default:
        return
    }
  }

  const handleActionInvoice = (paymentMethod, pdfUrl) => {
    if (paymentMethod === 'Invoice') {
      const recipientEmail = 'hello@elevatedliving.com'
      const subject = t('chat:invoicesFullView.invoicePaymentSubject')
      const mailtoLink = `mailto:${recipientEmail}?subject=${encodeURIComponent(subject)}`
      window.location.assign(mailtoLink)
    } else {
      window.open(pdfUrl, '_blank')
    }
  }

  const handleActionSystemInbox = ({ data, mode }) => {
    switch (mode) {
      case 'settings':
        return setSelectedSystemNumberRow(data)
      default:
        return null
    }
  }

  const handleCancelSubscription = () => {
    try {
      API.cancelSmsSubscription(currentBuilding.id).then(({ data }) => {
        dispatchUpdateSmsPackage(data)
        getInvoicesWithCount(1, currentBuilding.id)
        setCancelSubWindowVisibility(false)
        displaySnackbar(t('chat:inboxSettings.subscriptionCanceledSuccess'))
      })
    } catch {
      setCancelSubWindowVisibility(false)
      displaySnackbar(t('chat:inboxSettings.subscriptionCancelError'), true)
    }
  }

  const handleSelectSmsPackage = (selectedPackage) => {
    selectPackageSms(selectedPackage)
    setSmsPackageWindowVisibility(false)
    setPaymentModalVisible(true)
  }

  const handleUpdateSubscription = async (body) => {
    const updatedBody = {
      ...body,
      creditPackageId: selectedPackageSms.id,
    }

    try {
      const { data } = await API.updateSmsSubscription(currentBuilding.id, updatedBody)
      if (userRole === 'Administrator') {
        dispatchUpdateSmsPackage(data)
        displaySnackbar(t('chat:inboxSettings.subscriptionUpdatedSuccess'))
      }
      getInvoicesWithCount(1, currentBuilding.id)
      return data
    } catch (error) {
      displaySnackbar(t('chat:inboxSettings.subscriptionUpdateError'), true)
      throw new Error(error)
    }
  }

  const handleUpdateChatInboxes = (data) => {
    dispatchUpdateChatInboxes(data)
    getInvoicesWithCount(1, currentBuilding.id)
  }

  useEffect(() => {
    ;(async () => {
      try {
        const { data } = await API.getSmsPackages()
        if (data && data.length) {
          setNumberPackage(data[0])
          data.shift()
          setSmsPackages(data)
        }
      } catch (error) {
        console.error('Error fetching SMS packages:', error)
      }
    })()
  }, [])

  const isBuildingSmsPackageNeedLoad = buildingSmsPackage?.remainingSmsCredit < 100
  const isBuildingSmsPackageActive = buildingSmsPackage?.status !== 'canceled'
  const timezone = currentBuilding.timezone || 'America/Chicago'
  const selectedInbox = chatInboxes?.find((inbox) => inbox.id === selectedInboxId)

  const renderContent = () => {
    if (selectedInbox) {
      return (
        <SelectedInboxDetails
          inbox={selectedInbox}
          timezone={timezone}
          userRole={userRole}
          building={currentBuilding}
          updateChatInboxes={handleUpdateChatInboxes}
          numberPackageToBuy={numberPackage}
          onClose={() => setSelectedInboxId(null)}
        />
      )
    }
    if (invoicesFullView) {
      return (
        <InvoicesFullView
          invoices={recentInvoices}
          buildingId={currentBuilding.id}
          timezone={timezone}
          onClose={() => setInvoicesFullView(false)}
        />
      )
    }
    if (selectedSystemNumberRow) {
      return (
        <SelectedSystemNumber
          {...selectedSystemNumberRow}
          buildingId={currentBuilding.id}
          numberPackageToBuy={numberPackage}
          timezone={timezone}
          userRole={userRole}
          onClose={() => setSelectedSystemNumberRow(null)}
        />
      )
    }

    return (
      <React.Fragment>
        <div className="inbox__settings-sms-package-container">
          <div className="left-side">
            <PackageStatusInfo
              buildingSmsPackage={buildingSmsPackage}
              isBuildingSmsPackageActive={isBuildingSmsPackageActive}
              isBuildingSmsPackageNeedLoad={isBuildingSmsPackageNeedLoad}
              timezone={timezone}
              tooltipInfo={tooltipInfo}
            />
          </div>
          <div className="right-side gap-25">
            {!isBuildingSmsPackageActive ? (
              <button
                className="button button-black is-block height-40"
                onClick={() => setSmsPackageWindowVisibility(true)}
              >
                {t('chat:inboxSettings.buyPackage')}
              </button>
            ) : (
              <React.Fragment>
                <button
                  className="button button-gray is-block height-40"
                  onClick={() => setCancelSubWindowVisibility(true)}
                >
                  {t('chat:inboxSettings.cancelSubscription')}
                </button>
                <button
                  className="button button-black is-block height-40"
                  onClick={() => setSmsPackageWindowVisibility(true)}
                >
                  {t('chat:inboxSettings.updatePackage')}
                </button>
              </React.Fragment>
            )}
          </div>
        </div>
        <div className="inbox__settings-navbar">
          <div className="left-side">{t('chat:inboxSettings.inboxes')}</div>
          <div className="right-side gap-20">
            <SearchField
              placeholder={t('chat:inboxSettings.searchInboxes')}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
              }}
            />
            {currentBuilding?.chatCustom && (
              <button
                className="button button-primary"
                onClick={() => {
                  setInboxChatFormModalVisible(true)
                }}
              >
                <PlusCircleIcon size={20} style={{ marginRight: 4 }} />
                {t('chat:inboxSettings.addInboxChat')}
              </button>
            )}
          </div>
        </div>
        <div className="inbox__settings-body">
          <div className="inboxes-main-table">
            {chatInboxes && chatInboxes.length ? (
              <FeaturedTable
                columns={chatInboxesTableColumns(timezone, handleActionInbox)}
                data={chatInboxes.filter((inbox) =>
                  inbox.name.toLowerCase().includes(search.toLowerCase()),
                )}
              />
            ) : (
              <div className="no-data">{t('chat:inboxSettings.noDataToDisplay')}</div>
            )}
          </div>
        </div>
        <SectionSystemNumbers
          buildingId={currentBuilding.id}
          timezone={timezone}
          numberPackage={numberPackage}
          userRole={userRole}
          handleActionSystemInbox={handleActionSystemInbox}
        />
        <div className="inbox__settings-navbar">
          <div className="left-side">{t('chat:inboxSettings.recentInvoices')}</div>
          <div className="right-side">
            {recentInvoices && recentInvoices.length && (
              <button
                className="button button-primary is-block"
                onClick={() => setInvoicesFullView(true)}
              >
                {t('chat:inboxSettings.viewAll')}
              </button>
            )}
          </div>
        </div>
        <div className="inbox__settings-body">
          <div className="inboxes-main-table">
            {recentInvoices && recentInvoices.length ? (
              <FeaturedTable
                columns={chatInvoicesTableColumns(timezone, handleActionInvoice)}
                data={recentInvoices ? recentInvoices.slice(0, 4) : null}
              />
            ) : (
              <div className="no-data">{t('chat:inboxSettings.noDataToDisplay')}</div>
            )}
          </div>
        </div>
      </React.Fragment>
    )
  }

  return (
    <div className="inbox__settings">
      {renderContent()}
      <ConfirmPopup
        title={t('chat:inboxSettings.cancelSubscriptionConfirmTitle')}
        body={t('chat:inboxSettings.cancelSubscriptionBody', {
          endDate: buildingSmsPackage?.endDate
            ? formatInTimeZone(buildingSmsPackage.endDate, timezone, 'MM/dd/yyyy')
            : '',
        })}
        cancelText={t('common:actions.close')}
        confirmText={t('chat:selectedSystemNumber.cancelSubscription')}
        isOpen={cancelSubWindowVisibility}
        cancelCallback={() => setCancelSubWindowVisibility(false)}
        confirmCallback={handleCancelSubscription}
        className="is-warning"
      />
      {smsPackageWindowVisibility && (
        <SmsPackagesWindow
          smsPackages={smsPackages}
          currentSmsPackage={buildingSmsPackage}
          handleSelect={handleSelectSmsPackage}
          handleClose={() => setSmsPackageWindowVisibility(false)}
        />
      )}
      {selectedPackageSms && (
        <Elements stripe={stripePromise}>
          <PaymentModal
            isOpen={paymentModalVisible}
            userRole={userRole}
            buildingId={currentBuilding.id}
            purchaseItemTitle={`SMS Package "${
              selectedPackageSms.packageName
            } ${selectedPackageSms.packageCredit.toLocaleString(undefined, {
              useGrouping: true,
            })} SMS"`}
            purchaseItemPrice={selectedPackageSms.monthlyPrice}
            handleSubmit={handleUpdateSubscription}
            handleClose={() => setPaymentModalVisible(false)}
          />
        </Elements>
      )}
      {inboxChatFormModalVisible && (
        <InboxChatFormModal
          isOpen={inboxChatFormModalVisible}
          onClose={() => {
            setInboxChatFormModalVisible(false)
            setSelectedCustomInbox(null)
          }}
          buildingId={currentBuilding.id}
          chatInboxes={chatInboxes || []}
          inbox={selectedCustomInbox}
          onAdd={() => {
            refetch()
            displaySnackbar(
              selectedCustomInbox
                ? t('chat:inboxSettings.inboxUpdateSuccess')
                : t('chat:inboxSettings.inboxCreateSuccess'),
            )
          }}
        />
      )}
    </div>
  )
}

InboxSettings.propTypes = {
  recentInvoices: PropTypes.array,
  currentBuilding: PropTypes.object,
  dispatchUpdateChatInboxes: PropTypes.func,
  dispatchUpdateSmsPackage: PropTypes.func,
  userRole: PropTypes.string,
  getInvoicesWithCount: PropTypes.func,
  dispatchGetChatInboxes: PropTypes.func,
}

export default connect(
  (state) => ({
    userRole: selectUserRole(state),
    recentInvoices: selectInvoices(state),
  }),
  {
    dispatchUpdateChatInboxes,
    dispatchUpdateSmsPackage,
    getInvoicesWithCount,
    dispatchGetChatInboxes,
  },
)(InboxSettings)
